import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, ShieldCheck, Loader2, KeyRound } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";

/**
 * Forced password change screen shown on a parent's first login.
 * On success, updates the auth context's `must_change_password` flag and
 * navigates to /parent.
 */
export default function ParentChangePasswordPage() {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!user || user.role !== "parent") {
    navigate("/", { replace: true });
    return null;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 6) { setError("New password must be at least 6 characters."); return; }
    if (newPassword !== confirm) { setError("New password and confirmation do not match."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/parent/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      updateUser(data);
      toast.success("Password updated. Welcome aboard!");
      navigate("/parent", { replace: true });
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#EFF6FF] via-white to-[#F5F3FF] flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-3xl border border-slate-200/70 shadow-floating p-8" data-testid="parent-change-password-page">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-11 h-11 rounded-2xl bg-[#DBEAFE] flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-[#2563EB]" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight">Set a new password</h1>
            <p className="text-sm text-slate-500">Welcome, {user.name}</p>
          </div>
        </div>
        <p className="text-sm text-slate-600 mt-3 mb-5 leading-relaxed">
          {user.must_change_password
            ? "Since this is your first time signing in, please replace the temporary password (your child's birthday) with one only you know."
            : "Update your parent-portal password below. You'll use the new one next time you sign in."}
        </p>

        <form onSubmit={onSubmit} className="space-y-4" data-testid="parent-change-password-form">
          <div>
            <Label htmlFor="curr-pw" className="font-heading">{user.must_change_password ? "Temporary password (child's birthday DDMMYYYY)" : "Current password"}</Label>
            <div className="relative mt-1.5">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input id="curr-pw" type="password" required value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="rounded-2xl h-12 pl-10" data-testid="parent-curr-pw-input" />
            </div>
          </div>
          <div>
            <Label htmlFor="new-pw" className="font-heading">New password</Label>
            <div className="relative mt-1.5">
              <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input id="new-pw" type="password" required minLength={6} value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="rounded-2xl h-12 pl-10" data-testid="parent-new-pw-input" />
            </div>
            <p className="text-xs text-slate-400 mt-1">Minimum 6 characters.</p>
          </div>
          <div>
            <Label htmlFor="conf-pw" className="font-heading">Confirm new password</Label>
            <div className="relative mt-1.5">
              <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input id="conf-pw" type="password" required minLength={6} value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="rounded-2xl h-12 pl-10" data-testid="parent-conf-pw-input" />
            </div>
          </div>

          {error && (
            <div className="rounded-2xl bg-[#FFEBEE] text-[#C62828] px-4 py-3 text-sm" data-testid="parent-pw-error">
              {error}
            </div>
          )}

          <Button type="submit" disabled={loading}
            className="w-full h-12 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-heading font-semibold shadow-floating"
            data-testid="parent-save-pw-btn">
            {loading ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Updating…</> : "Save & continue"}
          </Button>
          {user.must_change_password ? (
            <Button type="button" variant="ghost" onClick={() => { logout(); navigate("/"); }}
              className="w-full text-slate-500 hover:text-foreground rounded-full"
              data-testid="parent-pw-cancel">
              Cancel and sign out
            </Button>
          ) : (
            <Button type="button" variant="ghost" onClick={() => navigate("/parent")}
              className="w-full text-slate-500 hover:text-foreground rounded-full"
              data-testid="parent-pw-back">
              Back to dashboard
            </Button>
          )}
        </form>
      </div>
    </div>
  );
}
